<template>
  <div class="chatbot-container wrapper candidates-page">
    <!-- Enhanced Header Section -->
    <PageHeader
      title="Financing Candidates"
      subtitle="Select a financing candidate to review their assessment status and credit score."
    >
      <template #actions>
        <BaseButton
          stylevariant="secondary"
          :cus_style="{ width: 'auto', padding: '0.65rem 1.25rem', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
          @click="$router.push({ name: 'chatbot-config' })"
        >
          <i class="las la-cog"></i>
          Configuration
        </BaseButton>
        <BaseButton
          stylevariant="primary"
          :cus_style="{ width: 'auto', padding: '0.65rem 1.25rem', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }"
          @click="openAddCandidateForm"
        >
          <i class="las la-plus"></i>
          Add New Candidate
        </BaseButton>
      </template>
    </PageHeader>

    <!-- Main Content -->
    <div class="main-content">
      <div class="content-wrapper">
        <div v-if="!error" class="candidate-controls">
          <div class="candidate-filters">
            <div class="filter-group">
              <label for="candidate-status-filter">Status</label>
              <ChatbotSelect
                id="candidate-status-filter"
                v-model="filters.status"
                :options="statusFilterOptions"
                :reduce="(o) => o.value"
                placeholder="All statuses"
                @input="onCandidateFiltersChange"
              />
            </div>

            <div class="filter-group">
              <label for="candidate-score-sort">Sort</label>
              <ChatbotSelect
                id="candidate-score-sort"
                v-model="filters.scoreSort"
                :options="scoreSortOptions"
                :reduce="(o) => o.value"
                placeholder="Newest first"
                @input="onCandidateFiltersChange"
              />
            </div>
          </div>

          <button
            v-if="hasActiveFilters"
            type="button"
            class="clear-filters-btn"
            @click="clearCandidateFilters"
          >
            Clear filters
          </button>
        </div>

        <!-- Empty State -->
        <div
          class="empty-state text-center"
          v-if="!loading && !error && candidates.length === 0"
        >
          <i class="las la-user la-5x text-muted mb-3"></i>
          <div class="empty-title">
            {{ hasActiveFilters ? 'No candidates match these filters' : 'No financing candidates found' }}
          </div>
          <div class="empty-subtitle">
            {{ hasActiveFilters ? 'Try changing the status or score filters.' : 'Click "Add New Candidate" to get started.' }}
          </div>
        </div>

        <!-- Error State -->
        <div
          class="empty-state text-center"
          v-if="error"
        >
          <i class="las la-exclamation-triangle la-5x text-danger mb-3"></i>
          <div class="empty-title text-danger">
            {{ error }}
          </div>
          <button 
            class="btn btn-primary btn-sm mt-3" 
            @click="loadCandidates"
          >
            <i class="las la-redo mr-1"></i>
            Try Again
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state text-center">
          <BaseSpinner />
          <div class="empty-title">Loading candidates...</div>
        </div>

        <!-- Enhanced Candidates Grid -->
        <div v-if="!loading && !error && candidates.length > 0" class="candidates-grid">
          <!-- Grid Header -->
          <div class="grid-header">
            <div class="header-row">
              <div class="header-cell candidate-col">
                <span>Candidate</span>
              </div>
              <div class="header-cell status-col">
                <span>Status</span>
              </div>
              <div class="header-cell score-col">
                <span>Score</span>
              </div>
              <div class="header-cell actions-col"></div>
            </div>
          </div>

          <!-- Grid Body -->
          <div class="grid-body">
            <div 
              v-for="candidate in candidates" 
              :key="candidate.id"
              class="candidate-card"
              @click="selectCandidate(candidate)"
            >
              <div class="candidate-cell candidate-col">
                <div class="candidate-info">
                  <div class="candidate-avatar">
                    <div class="avatar-placeholder">
                      <span class="avatar-initials">{{ getInitials(candidate.name) }}</span>
                    </div>
                  </div>
                  <div class="candidate-details">
                    <div class="candidate-name">{{ candidate.name || 'N/A' }}</div>
                  </div>
                </div>
              </div>

              <div class="candidate-cell status-col" data-label="Status">
                <span class="status-badge" :class="getStatusClass(candidate.status)">
                  {{ formatStatus(candidate.status) }}
                </span>
              </div>

              <div class="candidate-cell score-col" data-label="Score">
                <span class="score-value" :class="{ empty: candidate.credit_score === null || candidate.credit_score === undefined }">
                  {{ formatScore(candidate.credit_score) }}
                </span>
              </div>

              <div class="candidate-cell actions-col" @click.stop>
                <button class="btn-delete" @click="deleteCandidate(candidate)" title="Delete">
                  <i class="las la-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Financing Candidate Form Modal -->
    <FinancingCandidateForm 
      ref="candidateForm"
      @candidate-added="onCandidateAdded"
    />
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import FinancingCandidateForm from "./components/FinancingCandidateForm.vue";
import PageHeader from "./components/PageHeader.vue";
import BaseButton from "@/components/UI/BaseButton.vue";
import BaseSpinner from "@/components/UI/BaseSpinner.vue";
import ChatbotSelect from "./components/ChatbotSelect.vue";

export default {
  name: 'Chatbot',
  components: {
    FinancingCandidateForm,
    PageHeader,
    BaseButton,
    BaseSpinner,
    ChatbotSelect
  },
  data() {
    return {
      candidates: [],
      loading: false,
      error: null,
      filters: {
        status: '',
        scoreSort: ''
      },
      statusFilterOptions: [
        { value: '', label: 'All statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' }
      ],
      scoreSortOptions: [
        { value: '', label: 'Newest first' },
        { value: 'score_desc', label: 'Score: high to low' },
        { value: 'score_asc', label: 'Score: low to high' }
      ]
    }
  },
  computed: {
    hasActiveFilters() {
      return Boolean(this.filters.status || this.filters.scoreSort);
    }
  },
  async mounted() {
    await this.loadCandidates();
  },
  methods: {
    async loadCandidates() {
      this.loading = true;
      try {
        const data = await ChatbotService.getFinancingCandidates(this.getCandidateQueryParams());
        this.candidates = Array.isArray(data) ? data : (data.candidates || data.data || []);
      } catch (err) {
        this.error = 'Failed to load financing candidates.';
        console.error('Error loading candidates:', err);
      } finally {
        this.loading = false;
      }
    },

    getCandidateQueryParams() {
      const params = {};

      if (this.filters.status) {
        params.status = this.filters.status;
      }

      if (this.filters.scoreSort) {
        params.sort = 'score';
        params.order = this.filters.scoreSort === 'score_asc' ? 'asc' : 'desc';
      }

      return params;
    },

    async onCandidateFiltersChange() {
      await this.loadCandidates();
    },

    async clearCandidateFilters() {
      this.filters = {
        status: '',
        scoreSort: ''
      };
      await this.loadCandidates();
    },
    
    selectCandidate(candidate) {
      this.$router.push({ 
        name: 'chatbot-automated-assessment-input',
        params: { 
          candidateId: candidate.id
        }
      });
    },
    
    openAddCandidateForm() {
      this.$refs.candidateForm.open();
    },
    
    onCandidateAdded(candidate) {
      this.loadCandidates();
      this.$bvToast.toast('Financing candidate added successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true,
        autoHideDelay: 3000
      });
    },

    async deleteCandidate(candidate) {
      const name = candidate.name || 'this candidate';
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${name}"?`,
        {
          title: 'Confirm delete',
          buttonSize: 'md',
          okTitle: 'Delete',
          okVariant: 'dark btn-pill font-weight-bolder',
          cancelTitle: 'Cancel',
          cancelVariant: 'secondary btn-pill font-weight-bolder',
          centered: true
        }
      );
      if (!confirmed) return;
      try {
        await ChatbotService.deleteFinancingCandidate(candidate.id);
        this.loadCandidates();
        this.$showAlert('Candidate deleted.', 'success');
      } catch (err) {
        this.$showAlert(err.response?.data?.message || 'Delete failed.', 'danger');
      }
    },
    
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    formatStatus(status) {
      if (!status) return 'Unknown';
      return String(status)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },

    getStatusClass(status) {
      return `status-${status || 'unknown'}`;
    },

    formatScore(score) {
      const numericScore = Number(score);
      if (!Number.isFinite(numericScore)) return 'Not run';
      return numericScore.toFixed(1);
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.chatbot-container {
  padding: 0;
}

// Main wrapper styling with modern gradient background
.wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 1rem;
  overflow-x: hidden;
  max-width: 100%;
}


.landing-btn {
  font-family: $brand-font-medium;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  
  &.primary {
    background: $color-primary;
    color: $color-secondary;
    box-shadow: 0 2px 12px rgba(115, 243, 169, 0.3);
    
    &:hover {
      background: adjust-color($color-primary, $lightness: -5%);
      box-shadow: 0 4px 20px rgba(115, 243, 169, 0.4);
      transform: translateY(-2px);
    }
  }
  
  &:active {
    transform: translateY(0);
  }
}

.btn-icon {
  font-size: 1.1rem;
}

// Main content area
.main-content {
  .content-wrapper {
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
}

.candidate-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1.25rem 1.5rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e9ecef;
}

.candidate-filters {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 190px;

  label {
    color: #6c757d;
    padding-left: 0.45rem;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-family: $brand-font-medium;
  }

  ::v-deep .cselect__trigger {
    height: 38px;
    padding: 0.45rem 0.75rem;
    font-size: 0.875rem;
  }
}

.clear-filters-btn {
  height: 38px;
  padding: 0 0.85rem;
  border: 1px solid #d3d9e4;
  border-radius: 8px;
  background: #ffffff;
  color: #4a5568;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: $brand-font-medium;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #f8f9fa;
    border-color: #c2cad8;
  }
}

// Enhanced candidates grid
.candidates-grid {
  .grid-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 0.75rem 0;
    border-bottom: 1px solid #dee2e6;
    position: sticky;
    top: 0;
    z-index: 10;
    
    .header-row {
      display: grid;
      grid-template-columns: minmax(220px, 2fr) 150px 120px 56px;
      gap: 0.75rem;
      padding: 0 1rem;
      align-items: center;
    }
    
    .header-cell {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.9rem;
      font-weight: 700;
      color: #495057;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      
      i {
        font-size: 0.9rem;
        color: #212529;
      }

      &.score-col {
        justify-content: center;
        text-align: center;
      }
    }
  }
  
  .grid-body {
    padding: 0.5rem;
    
    .candidate-card {
      display: grid;
      grid-template-columns: minmax(220px, 2fr) 150px 120px 56px;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      margin-bottom: 0.5rem;
      background: white;
      border-radius: 0.5rem;
      border: 1px solid #dee2e6;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
      cursor: pointer;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, $color-primary, lighten($color-primary, 20%));
        transform: scaleX(0);
        transition: transform 0.3s ease;
        transform-origin: left;
      }
      
      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 40px rgba(33, 37, 41, 0.15);
        border-color: $color-primary;
        
        &::before {
          transform: scaleX(1);
        }
      }
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    .candidate-cell {
      display: flex;
      align-items: center;
      min-height: 1.5rem;
      overflow: hidden;
      padding: 0.125rem 0;
    }

    .status-col {
      justify-content: flex-start;
    }

    .score-col {
      justify-content: center;
      position: relative;
      left: 6px;
      text-align: center;
    }

    .actions-col {
      justify-content: flex-end;
    }

    .btn-delete {
      padding: 0.35rem 0.5rem;
      border: none;
      background: transparent;
      color: #e53e3e;
      cursor: pointer;
      border-radius: 6px;
      font-size: 1.1rem;

      &:hover {
        i{
          color: darken(#e53e3e, 10%);
        }
      }
    }
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-family: $brand-font-semi-bold;
  white-space: nowrap;
}

.status-pending {
  background: #fff7ed;
  color: #c2410c;
}

.status-approved {
  background: #e7f7ee;
  color: #12924f;
}

.status-rejected {
  background: #fee2e2;
  color: #b91c1c;
}

.status-unknown {
  background: #eef1f6;
  color: #4b5565;
}

.score-value {
  color: #1e293b;
  font-size: 0.95rem;
  font-weight: 700;
  font-family: $brand-font-medium;
  font-variant-numeric: tabular-nums;
}

.score-value.empty {
  color: #94a3b8;
  font-size: 0.85rem;
  font-weight: 600;
}

// Enhanced candidate info with avatar
.candidate-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  
  .candidate-avatar {
    width: 32px;
    height: 32px;
    border-radius: 0.5rem;
    overflow: hidden;
    position: relative;
    flex-shrink: 0;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    
    .avatar-placeholder {
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, $color-primary, darken($color-primary, 15%));
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.375rem;
      
      .avatar-initials {
        color: $color-secondary;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
      }
    }
  }
  
  .candidate-details {
    min-width: 0;
    flex: 1;
    
    .candidate-name {
      font-weight: 700;
      font-size: 1rem;
      color: #1e293b;
      line-height: 1.1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

// Enhanced email info
.email-info {
  width: 100%;
  
  .email-link {
    color: #212529;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0.2rem 0.2rem;
    margin-top: 0.2rem;
    border-radius: 0.375rem;
    background: rgba(33, 37, 41, 0.1);
    display: inline-block;
    max-width: 100%;
    
    &:hover {
      background: rgba(33, 37, 41, 0.2);
      color: #000000;
      text-decoration: none;
      transform: scale(1.05);
    }
  }
}

// Phone and notes info
.phone-info, .notes-info {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  
  .phone-number, .notes-text {
    font-size: 0.8rem;
    color: #212529;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  
  .notes-text {
    font-style: italic;
    color: #6c757d;
  }
}

// Enhanced empty states
.empty-state, .loading-state {
  padding: 2.5rem 1.5rem;
  text-align: center;
  
  i {
    opacity: 0.4;
    margin-bottom: 1rem;
    color: #6c757d;
  }

  .lds-ring {
    margin: 0 auto 1rem;
  }
  
  .empty-title {
    font-size: 1.125rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
    color: #212529;
  }
  
  .empty-subtitle {
    font-size: 0.9rem;
    opacity: 0.8;
    color: #495057;
  }
}

// Responsive design
@media (max-width: 1200px) {
  .candidates-grid {
    .grid-header .header-row,
    .grid-body .candidate-card {
      grid-template-columns: minmax(200px, 2fr) 140px 110px 56px;
      gap: 1rem;
    }
  }
}

@media (max-width: 992px) {
  .wrapper {
    padding: 0.75rem;
  }
  
  .page-header {
    padding: 1rem;
    
    .header-content {
      flex-direction: column;
      gap: 1rem;
    }
    
    .header-title .title {
      font-size: 1.375rem;
    }
  }
  
  .candidates-grid {
    .grid-header .header-row,
    .grid-body .candidate-card {
      grid-template-columns: minmax(180px, 2fr) 130px 100px 48px;
      gap: 1rem;
    }
  }
}

@media (max-width: 768px) {
  .wrapper {
    padding: 0.75rem;
  }
  
  .page-header {
    padding: 1rem;
    margin-bottom: 1rem;
    
    .header-title .title {
      font-size: 1.25rem;
    }
    
    .header-title .subtitle {
      font-size: 0.8rem;
    }
  }
  
  .candidates-grid {
    .grid-header {
      display: none;
    }
    
    .grid-body {
      padding: 0.5rem;
      
      .candidate-card {
        grid-template-columns: 1fr;
        gap: 1rem;
        padding: 1.5rem;
        
        .candidate-cell {
          width: 100%;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f8f9fa;
          
          &:last-child {
            border-bottom: none;
          }
          
          &:before {
            content: attr(data-label);
            font-weight: 700;
            font-size: 0.75rem;
            color: #212529;
            text-transform: uppercase;
            margin-right: 1rem;
            min-width: 80px;
          }
          
          &.candidate-col {
            justify-content: flex-start;
            border-bottom: 2px solid #f8f9fa;
            margin-bottom: 0.5rem;
            
            &:before {
              display: none;
            }
          }

          &.actions-col {
            justify-content: flex-end;

            &:before {
              display: none;
            }
          }
        }
        
        .candidate-info {
          width: 100%;
          margin-bottom: 0.5rem;
        }
      }
    }
  }

  .candidate-controls {
    align-items: stretch;
    justify-content: flex-start;
  }

  .candidate-filters {
    width: 100%;
  }

  .filter-group,
  .clear-filters-btn {
    width: 100%;
  }
}

@media (max-width: 576px) {
  .wrapper {
    padding: 0.5rem;
  }
  
  .page-header {
    padding: 0.875rem;
    
    .header-title .title {
      font-size: 1.125rem;
    }
  }
  
  .candidate-info {
    .candidate-avatar {
      width: 28px;
      height: 28px;
    }
    
    .candidate-details .candidate-name {
      font-size: 0.8rem;
    }
  }
}
</style>
